/**
 * German for the requirement vocabulary — the three types and the five statuses.
 *
 * DELIBERATELY NOT the document-category maps. `TenancyDocumentsSection` and
 * `AdminTenantDetailPage` each carry a map of DOCUMENT categories, and one of
 * them happens to translate a category called `insurance` as "Versicherung".
 * A requirement is not a document category: `liability_insurance` is the
 * obligation to hold private liability cover, and reading it through a generic
 * "insurance" entry would name the wrong thing on screen. So this file starts
 * from the requirement catalogue and nothing else, and there is no generic
 * `insurance` key here to fall into.
 *
 * AN UNKNOWN VALUE FALLS THROUGH TO ITSELF. The backend owns both vocabularies
 * and may grow them; showing the raw value is honest about a word we cannot
 * translate yet, where a friendly catch-all ("Sonstiges", "Unbekannt") would
 * assert something the frontend does not know.
 */

export const REQUIREMENT_TYPE_LABELS = {
  residence_registration: "Wohnsitzanmeldung",
  liability_insurance: "Privathaftpflicht",
  deposit: "Mietkaution",
};

export const REQUIREMENT_STATUS_LABELS = {
  overdue: "Überfällig",
  open: "Offen",
  fulfilled: "Erfüllt",
  suspended: "Ausgesetzt",
  not_required: "Nicht erforderlich",
};

/**
 * Badge tone per status. Text carries the meaning; the tone reinforces it.
 *
 * `suspended` and `not_required` are both quiet on purpose — neither is a task
 * — but they are not the same quiet: suspended is muted (the tenancy ended, the
 * question is moot) and not_required is neutral (somebody decided).
 */
export const REQUIREMENT_STATUS_TONES = {
  overdue: "bad",
  open: "warn",
  fulfilled: "positive",
  suspended: "muted",
  not_required: "neutral",
};

export function requirementTypeLabel(value) {
  return REQUIREMENT_TYPE_LABELS[value] || String(value ?? "");
}

export function requirementStatusLabel(value) {
  return REQUIREMENT_STATUS_LABELS[value] || String(value ?? "");
}

export function requirementStatusTone(value) {
  return REQUIREMENT_STATUS_TONES[value] || "neutral";
}

/**
 * The six status cards, in the order an operator works through them: what is
 * late, what is pending, what is done, then the two that need no action.
 *
 * `key` is the field to read out of the backend's `summary`; `status` is the
 * value to send back as the filter. "Gesamt" clears the filter rather than
 * sending one, which is why its `status` is null.
 */
export const REQUIREMENT_FACETS = [
  { key: "total", status: null, label: "Gesamt", tone: "primary" },
  { key: "overdue", status: "overdue", label: "Überfällig", tone: "bad" },
  { key: "open", status: "open", label: "Offen", tone: "warn" },
  { key: "fulfilled", status: "fulfilled", label: "Erfüllt", tone: "good" },
  { key: "suspended", status: "suspended", label: "Ausgesetzt", tone: "neutral" },
  {
    key: "not_required",
    status: "not_required",
    label: "Nicht erforderlich",
    tone: "neutral",
  },
];

/** The type filter's options. Built from the label map so a rename cannot
 *  leave a dropdown behind saying the old word. */
export const REQUIREMENT_TYPE_OPTIONS = [
  { value: "", label: "Alle Nachweise" },
  ...["residence_registration", "liability_insurance", "deposit"].map((value) => ({
    value,
    label: REQUIREMENT_TYPE_LABELS[value],
  })),
];
